import React, { useEffect, useRef, useState } from "react";
import logger from "@/lib/logger";
import { Play, Pause } from "@phosphor-icons/react";
import { toast } from "sonner";

// Module-level singleton registry: only ONE WaveformPlayer plays at a time.
// When a player starts, it asks every other registered audio element to
// pause. This stops the "everything mixes together" bug when tapping
// multiple voice comments on the same post.
const _registered = new Set();
function _pauseAllExcept(self) {
  _registered.forEach((entry) => {
    if (entry !== self) entry.pause();
  });
}

// Deterministic pseudo-random heights per src (so bars look consistent per audio)
function genHeights(seedStr, n = 28) {
  let h = 0;
  for (let i = 0; i < seedStr.length; i++) h = (h * 31 + seedStr.charCodeAt(i)) >>> 0;
  const arr = [];
  for (let i = 0; i < n; i++) {
    h = (h * 1664525 + 1013904223) >>> 0;
    const v = ((h % 100) / 100);
    arr.push(20 + v * 80); // 20% - 100%
  }
  return arr;
}

function fmt(s) {
  if (!s || isNaN(s) || s === Infinity) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}

export default function WaveformPlayer({ src, duration, compact = false, testid = "waveform-player", onPlayStateChange }) {
  const audioRef = useRef(null);
  const entryRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(duration || 0);
  const heights = React.useMemo(() => genHeights(src || "x", compact ? 22 : 28), [src, compact]);
  // Mirror local `playing` state out to any parent that wants to sync
  // sibling audio (e.g. background music tied to this voice clip).
  useEffect(() => { if (onPlayStateChange) onPlayStateChange(playing); }, [playing, onPlayStateChange]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCur(a.currentTime);
    const onLoaded = () => { if (!isNaN(a.duration) && a.duration !== Infinity) setDur(a.duration); };
    const onEnd = () => { setPlaying(false); setCur(0); };
    const onPause = () => setPlaying(false); // catches external pause (from singleton)
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", onPause);
    // Register this player with the singleton. `pause()` is what other
    // players invoke to silence us when they start playing.
    const entry = { pause: () => { try { a.pause(); } catch {} } };
    entryRef.current = entry;
    _registered.add(entry);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("pause", onPause);
      _registered.delete(entry);
      entryRef.current = null;
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else {
      // Singleton: silence every other voice clip before starting this one.
      _pauseAllExcept(entryRef.current);
      a.play()
        .then(() => setPlaying(true))
        .catch((err) => {
          setPlaying(false);
          logger.warn("waveform", "play() rejected", err);
          if (err?.name === "NotAllowedError") {
            toast.error("Tap once more to play — your browser blocks autoplay.");
          } else if (err?.name === "NotSupportedError") {
            toast.error("This audio format isn't supported on this browser.");
          } else {
            toast.error("Couldn't play this clip.");
          }
        });
    }
  };

  const progress = dur > 0 ? cur / dur : 0;

  return (
    <div data-testid={testid} className={`flex items-center gap-2 ${compact ? "p-1.5 pr-2.5" : "p-3"} rounded-full bg-secondary/70 border border-white/5`}>
      <button
        onClick={toggle}
        data-testid={`${testid}-toggle`}
        className={`shrink-0 ${compact ? "h-7 w-7" : "h-10 w-10"} rounded-full bg-primary text-primary-foreground flex items-center justify-center active:scale-95 transition glow-hover`}
        aria-label={playing ? "Pause" : "Play"}
      >
        {playing ? <Pause size={compact ? 12 : 18} weight="fill" /> : <Play size={compact ? 12 : 18} weight="fill" />}
      </button>

      <div className={`flex-1 flex items-center gap-[2px] ${compact ? "h-5" : "h-8"}`}>
        {heights.map((h, i) => {
          const active = i / heights.length <= progress;
          return (
            <div
              key={`wf-${i}-${h}`}
              className={`flex-1 rounded-full ${active ? "bg-primary" : "bg-white/15"} transition-colors`}
              style={{ height: `${h}%` }}
            />
          );
        })}
      </div>

      {/* While playing, show "elapsed / total" so the listener always knows
          how much of the clip is left. When paused, show only the total. */}
      <span className={`font-mono ${compact ? "text-[10px]" : "text-xs"} text-muted-foreground ${playing ? "w-[68px]" : "w-10"} text-right tabular-nums`}>
        {playing ? `${fmt(cur)} / ${fmt(dur)}` : fmt(dur)}
      </span>

      <audio ref={audioRef} src={src} preload="auto" playsInline />
    </div>
  );
}
